import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';
import { Textarea } from './Textarea';
import { Loader2, Sparkles } from 'lucide-react';
import {
  BaseAiTextEnrichResponse,
  EnrichInteractionApiRequest,
  EnrichInteractionResponse,
  InteractionType,
} from '@momentum/types';
import { UseMutationResult } from '@tanstack/react-query';

interface AIEnrichedTextAreaProps {
  value: string;
  onChange: (value: string) => void;
  mutation: UseMutationResult<
    BaseAiTextEnrichResponse,
    Error,
    EnrichInteractionApiRequest,
    unknown
  >;
  placeholder?: string;
  maxLength?: number;
  minLength?: number;
  disabled?: boolean;
  required?: boolean;
  rows?: number;
  className?: string;
  interactionType: InteractionType.FEEDBACK | InteractionType.PRAISE;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

export const AIEnrichedTextArea: React.FC<AIEnrichedTextAreaProps> = ({
  value,
  onChange,
  mutation,
  placeholder = '',
  maxLength = 500,
  minLength = 0,
  disabled = false,
  required = false,
  rows = 4,
  className = '',
  interactionType,
  onKeyDown,
}) => {
  const { t } = useTranslation('wizard');
  const [localValue, setLocalValue] = useState(value || '');
  const [aiResponse, setAiResponse] =
    useState<EnrichInteractionResponse | null>(null);
  const [showAiResponse, setShowAiResponse] = useState(false);

  // Update local state when external value changes
  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
    onChange(newValue);
  };

  const handleEnrichWithAI = async () => {
    if (!localValue.trim()) return;

    try {
      const response = await mutation.mutateAsync({
        text: localValue,
        type:
          interactionType === InteractionType.PRAISE ? 'I_SEE_U' : 'I_NEED_U',
      });

      setAiResponse(response);
      setShowAiResponse(true);
    } catch (error) {
      console.error('Failed to enrich feedback:', error);
    }
  };

  const handleUseAIResponse = () => {
    if (aiResponse) {
      setLocalValue(aiResponse.refinedText);
      onChange(aiResponse.refinedText);
      setShowAiResponse(false);
    }
  };

  const handleDiscardAIResponse = () => {
    setShowAiResponse(false);
    setAiResponse(null);
  };

  return (
    <div className="w-full space-y-4">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleEnrichWithAI}
            disabled={disabled || !localValue.trim() || mutation.isPending}
            className="flex items-center gap-2"
          >
            {mutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            {mutation.isPending
              ? t('aiTextArea.enrichInProgress')
              : t('aiTextArea.enrichButton')}
          </Button>
        </div>

        <div className="relative">
          <Textarea
            value={localValue}
            onChange={handleChange}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            maxLength={maxLength}
            minLength={minLength}
            rows={rows}
            className={`resize-none bg-white border-sm shadow-sm min-h-28 ${className}`}
          />

          {showAiResponse && aiResponse && (
            <div className="absolute inset-0 pointer-events-none">
              <div className="h-full w-full bg-gradient-to-r from-transparent via-transparent to-green-50 opacity-20" />
            </div>
          )}
        </div>

        {maxLength && (
          <div className="text-sm text-muted-foreground text-right">
            {localValue.length} / {maxLength}
          </div>
        )}
      </div>

      {showAiResponse && aiResponse && (
        <div className="space-y-3 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-green-800">
              {t('aiTextArea.aiVersion')}
            </h4>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                onClick={handleUseAIResponse}
                className="bg-green-600 hover:bg-green-700"
              >
                {t('aiTextArea.useAIVersion')}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDiscardAIResponse}
              >
                {t('aiTextArea.discard')}
              </Button>
            </div>
          </div>

          <div className="text-sm text-green-700 bg-white p-3 rounded border">
            {aiResponse.refinedText}
          </div>

          {aiResponse.explanation && (
            <div className="text-sm text-blue-600 bg-blue-50 p-3 rounded border">
              <strong>{t('aiTextArea.explanation')}</strong>{' '}
              {aiResponse.explanation}
            </div>
          )}
        </div>
      )}

      {mutation.error && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
          {t('aiTextArea.error')}
        </div>
      )}
    </div>
  );
};
